/**
 * One image slot on the merchant store form: drop a file, crop it to the shape
 * the rider app renders, and hand back base64 for the mutation.
 *
 * Reuses the location Media components rather than introducing a second upload
 * UI. Their presets already match the rider app: `icon` is 1:1 at 512x512 (the
 * logo, drawn as a 40px circle bordered in the store's primary color) and
 * `banner` is 3:1 at 1200x400 (drawn at 900x300).
 */

import React, { useCallback, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import ImageUploadDropzone from '../StationMedia/ImageUploadDropzone';
import ImageCropModal from '../StationMedia/ImageCropModal';
import type { StationImageType } from '../StationMedia/types';
import FormItem from '../Redesing/form-item';
import { toBase64 } from '../../util/toBase64';

export interface MerchantStoreImageFieldProps {
  label: string;
  description: string;
  /** Which crop preset to apply — 'icon' for the logo, 'banner' for the banner. */
  imageType: Extract<StationImageType, 'icon' | 'banner'>;
  /** Existing image to show when nothing new has been chosen. */
  currentUrl?: string | null;
  /**
   * Called once a file is cropped, with the raw base64 the mutation wants (no
   * data: prefix) and a local URL for previewing it. Null when cleared.
   */
  onChange: (value: { base64: string; previewUrl: string } | null) => void;
  hasError?: boolean;
  errorMessage?: string;
  disabled?: boolean;
}

const MerchantStoreImageField: React.FC<MerchantStoreImageFieldProps> = ({
  label,
  description,
  imageType,
  currentUrl,
  onChange,
  hasError,
  errorMessage,
  disabled,
}) => {
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const handleDrop = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) return;
    setOriginalFile(file);
    setCropSource(URL.createObjectURL(file));
  }, []);

  const handleCropComplete = useCallback(
    async (processed: File) => {
      // Revoke the source object URL before swapping in the cropped preview.
      if (cropSource) URL.revokeObjectURL(cropSource);
      setCropSource(null);
      setOriginalFile(null);
      const previewUrl = URL.createObjectURL(processed);
      setPreview(previewUrl);
      onChange({ base64: await toBase64(processed), previewUrl });
    },
    [cropSource, onChange]
  );

  const handleCropCancel = useCallback(() => {
    if (cropSource) URL.revokeObjectURL(cropSource);
    setCropSource(null);
    setOriginalFile(null);
  }, [cropSource]);

  const clear = useCallback(() => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    onChange(null);
  }, [preview, onChange]);

  const shown = preview ?? currentUrl ?? null;

  return (
    <>
      <FormItem
        label={label}
        description={description}
        content={
          <Stack spacing={1}>
            {shown && (
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box
                  component="img"
                  alt={label}
                  src={shown}
                  sx={{
                    maxHeight: imageType === 'icon' ? 96 : 120,
                    maxWidth: '100%',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    objectFit: 'contain',
                  }}
                />
                {preview && (
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={clear}
                    disabled={disabled}
                  >
                    Remove
                  </Button>
                )}
              </Stack>
            )}

            <ImageUploadDropzone
              onDrop={handleDrop}
              accept={['image/png', 'image/jpeg']}
              maxSize={10 * 1024 * 1024}
              maxFiles={1}
              disabled={disabled}
              compact
              label={shown ? 'Replace image' : 'Drop an image or click to choose'}
            />

            {hasError && errorMessage && (
              <Typography variant="caption" color="error">
                {errorMessage}
              </Typography>
            )}
          </Stack>
        }
      />

      {cropSource && originalFile && (
        <ImageCropModal
          open
          imageSrc={cropSource}
          imageType={imageType}
          originalFile={originalFile}
          onComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </>
  );
};

export default MerchantStoreImageField;
